import { useState, useRef, useEffect, useCallback, memo } from 'react'
import { motion, AnimatePresence, useDragControls } from 'framer-motion'
import { useVoiceCommand } from '../../hooks/useVoiceCommand'
import { useSpeech } from '../../hooks/useSpeech'
import { useAuth } from '../../hooks/useAuth'
import { apiFetch } from '../../services/api'
import { EASE_OUT, EASE_IN, SPRING } from '../../theme/motion'
import { MessageBubble, EmptyState, SuggestionChip, modelLabel } from './ChatMessages'
import ChatComposer from './ChatComposer'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''
const PHONE_Q = '(max-width: 767px)'
const SPEAK_KEY = 'yokan.chat.speak'

// Spoken answers to a confirmation card.
const YES = /^(ja|jo|jep|ok(ay)?|klar|gerne|bitte|mach( das| es)?|ausführen|bestätigen?|los)\b/i
const NO = /^(nein|nee|nö|stopp?|abbrechen|lass( es| das)?|nicht)\b/i

function usePhone() {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE_Q).matches)
  useEffect(() => {
    const mq = window.matchMedia(PHONE_Q)
    const on = () => setPhone(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return phone
}

/** The visible area above the on-screen keyboard, so the composer never hides behind it. */
function useVisualViewport(active) {
  const [vp, setVp] = useState(null)
  useEffect(() => {
    const vv = window.visualViewport
    if (!active || !vv) return
    const on = () => setVp({ height: vv.height, top: vv.offsetTop })
    on()
    vv.addEventListener('resize', on)
    vv.addEventListener('scroll', on)
    return () => { vv.removeEventListener('resize', on); vv.removeEventListener('scroll', on) }
  }, [active])
  return active ? vp : null
}

const readSpeak = () => { try { return localStorage.getItem(SPEAK_KEY) !== '0' } catch { return true } }
const writeSpeak = v => { try { localStorage.setItem(SPEAK_KEY, v ? '1' : '0') } catch { /* private mode */ } }
const newId = () => Date.now() + Math.random()

// ── Main chat panel ──────────────────────────────────────────────────────────
// Desktop: floating, resizable panel above the round button. Phone: a full-screen sheet
// that follows the keyboard and closes with a swipe down on its header.
function ChatPanel() {
  const phone = usePhone()
  const { user } = useAuth()
  const [enabled, setEnabled]     = useState(null)
  const [provider, setProvider]   = useState(null)
  const [open, setOpen]           = useState(false)
  const [maximized, setMaximized] = useState(false)
  const [messages, setMessages]   = useState([])
  const [input, setInput]         = useState('')
  const [loading, setLoading]     = useState(false)
  const [size, setSize]           = useState({ width: 380, height: 540 })
  const [resizing, setResizing]   = useState(false)
  const [speakOn, setSpeakOn]     = useState(readSpeak)
  const [atBottom, setAtBottom]   = useState(true)
  const [context, setContext]     = useState(null)   // suggestions for the house right now
  const dragRef   = useRef(null)
  const scrollRef = useRef(null)
  const inputRef  = useRef(null)
  const msgsRef   = useRef(messages)
  useEffect(() => { msgsRef.current = messages })
  const sheet = useDragControls()
  const vp = useVisualViewport(open && phone)
  const speech = useSpeech()

  useEffect(() => {
    apiFetch(`${API_BASE}/settings`)
      .then(r => r.json())
      .then(data => {
        setEnabled(data.ai?.enabled !== false)
        setProvider(data.ai?.provider ?? null)
      })
      .catch(() => setEnabled(true))
  }, [])

  // Context suggestions (server/services/ai/suggestions.js): when the chat opens and after
  // each answer — the answer may have changed the house. Event-driven, no polling.
  const loadContext = useCallback(() => {
    apiFetch(`${API_BASE}/ai/suggestions`)
      .then(r => (r.ok ? r.json() : { suggestions: [] }))
      .then(d => setContext(d.suggestions ?? []))
      .catch(() => {})
  }, [])
  useEffect(() => { if (open) loadContext() }, [open, loadContext])

  const onResizeStart = useCallback((e) => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startY: e.clientY, startW: size.width, startH: size.height }
    setResizing(true)
    function onMove(ev) {
      const { startX, startY, startW, startH } = dragRef.current
      setSize({
        width:  Math.min(Math.max(300, startW + startX - ev.clientX), window.innerWidth  - 48),
        height: Math.min(Math.max(320, startH + startY - ev.clientY), window.innerHeight - 144),
      })
    }
    function onUp() {
      setResizing(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [size])

  const updateLastMessage = useCallback((patch) => {
    setMessages(prev => {
      const last = prev[prev.length - 1]
      if (!last || last.role !== 'assistant') return prev
      return [...prev.slice(0, -1), { ...last, ...patch }]
    })
  }, [])

  // POST and stream the SSE answer into a new assistant message. Lines are buffered
  // across reads — a `data:` line split between two chunks used to be dropped.
  // Resolves with the final { text, confirm } so a voice turn can read it aloud.
  const streamInto = useCallback(async (path, body) => {
    setMessages(prev => [...prev, { role: 'assistant', text: '', streaming: true, id: newId() }])
    const final = { text: '', confirm: null }
    try {
      const r = await fetch(`${API_BASE}${path}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      })
      if (!r.ok) {
        const data = await r.json().catch(() => ({}))
        throw new Error(data.error ?? `HTTP ${r.status}`)
      }
      const reader = r.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop()
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let data
          try { data = JSON.parse(line.slice(6)) } catch { continue }
          if (data.error) throw new Error(data.error)
          const patch = { streaming: data.streaming !== false }
          if (data.response !== undefined) { patch.text = data.response; final.text = data.response }
          if (data.meta !== undefined)     patch.meta = data.meta
          if (data.status !== undefined)   patch.status = data.status
          if (data.actions !== undefined)  patch.actions = data.actions
          if (data.confirm !== undefined)  { patch.confirm = data.confirm; final.confirm = data.confirm }
          if (!patch.streaming)            patch.status = null
          updateLastMessage(patch)
        }
      }
      updateLastMessage({ streaming: false, status: null })
    } catch (e) {
      final.text = `Das hat nicht geklappt: ${e.message}`
      updateLastMessage({ text: final.text, error: true, streaming: false, status: null })
    }
    return final
  }, [updateLastMessage])

  // Read a voice turn's answer aloud; a confirmation question listens for "ja"/"nein" after.
  const voiceRef = useRef(null)
  const speakAnswer = useCallback((final) => {
    if (!speakOn || !speech.supported) return
    if (final.confirm) {
      speech.speak(`${final.text ? `${final.text} ` : ''}${final.confirm.summary}. Soll ich das machen?`, {
        onEnd: () => voiceRef.current?.start(),
      })
    } else if (final.text) speech.speak(final.text)
  }, [speakOn, speech])

  const sendMessage = useCallback(async (text, { voice = false } = {}) => {
    const trimmed = text.trim()
    if (!trimmed || loading) return
    speech.cancel()
    setInput('')
    const history = msgsRef.current.filter(m => m.text && !m.error).map(m => ({ role: m.role, text: m.text }))
    setMessages(prev => [...prev, { role: 'user', text: trimmed, voice, id: newId() }])
    setLoading(true)
    const final = await streamInto('/ai/command', { message: trimmed, history, stream: true })
    setLoading(false)
    loadContext()
    if (voice) speakAnswer(final)
  }, [loading, streamInto, speakAnswer, speech, loadContext])

  // Answer a confirmation card: mark it, then stream the rest of the turn as a new message.
  const answerConfirm = useCallback(async (id, approve, { voice = false } = {}) => {
    if (loading) return
    speech.cancel()
    setMessages(prev => prev.map(m => (m.confirm?.id === id ? { ...m, confirm: { ...m.confirm, answer: approve } } : m)))
    setLoading(true)
    const final = await streamInto('/ai/confirm', { id, approve })
    setLoading(false)
    loadContext()
    if (voice) speakAnswer(final)
  }, [loading, streamInto, speakAnswer, speech, loadContext])

  // A spoken "ja"/"nein" answers an open confirmation card; anything else is a new message.
  const onVoice = useCallback((text) => {
    const pending = [...msgsRef.current].reverse().find(m => m.confirm && m.confirm.answer == null)
    const t = text.trim()
    if (pending && YES.test(t)) return answerConfirm(pending.confirm.id, true, { voice: true })
    if (pending && NO.test(t)) return answerConfirm(pending.confirm.id, false, { voice: true })
    return sendMessage(t, { voice: true })
  }, [answerConfirm, sendMessage])

  const onVoiceError = useCallback((msg) => {
    setMessages(prev => [...prev, { role: 'assistant', text: msg, error: true, id: newId() }])
  }, [])

  const voice = useVoiceCommand({ onResult: onVoice, onError: onVoiceError })
  useEffect(() => { voiceRef.current = voice })
  const startVoice = useCallback(() => { speech.cancel(); voice.start() }, [speech, voice])

  const close = useCallback(() => {
    setOpen(false)
    speech.cancel()
    voice.cancel()
  }, [speech, voice])

  // Follow the conversation unless the person scrolled up to read.
  const count = messages.length
  const lastCount = useRef(count)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const grew = count !== lastCount.current
    lastCount.current = count
    if (grew || atBottom) el.scrollTo({ top: el.scrollHeight, behavior: grew ? 'smooth' : 'auto' })
  }, [messages, count, atBottom])
  const onScroll = e => {
    const el = e.currentTarget
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80)
  }

  // Desktop: focus the field on open. Phone: don't — the keyboard would cover the suggestions.
  useEffect(() => {
    if (open && !phone) {
      const t = setTimeout(() => inputRef.current?.focus(), 180)
      return () => clearTimeout(t)
    }
  }, [open, phone])

  // Phone sheet: freeze the page behind it.
  useEffect(() => {
    if (!(open && phone)) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open, phone])

  // Esc closes on desktop.
  useEffect(() => {
    if (!open || phone) return
    const onKey = e => { if (e.key === 'Escape' && !voice.listening) close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, phone, close, voice.listening])

  if (enabled === false) return null

  const lastModel = [...messages].reverse().find(m => m.meta?.model)?.meta.model
  const badge = modelLabel(lastModel) ?? ({ ollama: 'Gemma', gemini: 'Gemini', claude: 'Claude' })[provider] ?? null
  const firstName = user?.displayName || user?.name?.split(' ')[0] || null   // the name from Settings › Users

  const toggleSpeak = () => {
    const v = !speakOn
    setSpeakOn(v); writeSpeak(v)
    if (!v) speech.cancel()
  }

  const panelStyle = phone
    ? { top: vp?.top ?? 0, height: vp?.height ?? '100dvh' }
    : {
        bottom: maximized ? 24 : 120,
        right: 24,
        left: maximized ? 24 : 'auto',
        top: maximized ? 24 : 'auto',
        width:  maximized ? undefined : size.width,
        height: maximized ? undefined : size.height,
        userSelect: resizing ? 'none' : undefined,
      }

  return (
    <>
      {/* Round button — hidden behind the phone sheet while it is open */}
      <AnimatePresence>
        {!(phone && open) && (
          <motion.button
            key="chat-fab"
            onClick={() => (open ? close() : setOpen(true))}
            className={`chat-fab ${open ? 'is-open' : ''}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0, transition: { duration: 0.12 } }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={SPRING}
            title="YOKAN AI"
            aria-label={open ? 'Chat schließen' : 'Chat öffnen'}
          >
            {loading && !open && (
              <motion.span className="chat-fab-ring" animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }} />
            )}
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={open ? 'close' : 'open'}
                className="material-symbols-outlined"
                style={{ fontSize: '1.15rem' }}
                initial={{ rotate: -90, scale: 0.4, opacity: 0 }}
                animate={{ rotate: 0, scale: 1, opacity: 1 }}
                exit={{ rotate: 90, scale: 0.4, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 28 }}
              >{open ? 'close' : 'auto_awesome'}</motion.span>
            </AnimatePresence>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key={phone ? 'chat-sheet' : 'chat-panel'}
            role="dialog"
            aria-label="YOKAN AI"
            className={phone ? 'chat-shell chat-sheet' : 'chat-shell chat-float'}
            style={{ ...panelStyle, originX: 1, originY: 1 }}
            initial={phone ? { y: '100%' } : { opacity: 0, scale: 0.85, y: 24 }}
            animate={phone ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={phone
              ? { y: '100%', transition: { duration: 0.24, ease: EASE_IN } }
              : { opacity: 0, scale: 0.9, y: 16, transition: { duration: 0.16, ease: EASE_IN } }}
            transition={phone ? { type: 'spring', stiffness: 380, damping: 40 } : { type: 'spring', stiffness: 420, damping: 34 }}
            layout={!phone && !resizing}
            drag={phone ? 'y' : false}
            dragControls={sheet}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.7 }}
            onDragEnd={(_, info) => { if (info.offset.y > 120 || info.velocity.y > 600) close() }}
          >
            {!phone && !maximized && (
              <div className="chat-resize" onMouseDown={onResizeStart} title="Größe ändern">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <line x1="1" y1="9" x2="9" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="1" y1="5" x2="5" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
                </svg>
              </div>
            )}

            {/* Header — on the phone also the drag handle */}
            <div
              className={`chat-header ${phone ? 'chat-header-lg' : ''}`}
              onPointerDown={phone ? e => { if (!e.target.closest('button')) sheet.start(e) } : undefined}
              style={phone ? { touchAction: 'none' } : undefined}
            >
              {phone && <span className="chat-grabber" aria-hidden="true" />}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <motion.span
                  className="material-symbols-outlined text-primary"
                  style={{ fontSize: phone ? '1.25rem' : '1rem' }}
                  animate={loading ? { rotate: [0, 20, -15, 0], scale: [1, 1.15, 1] } : { rotate: 0, scale: 1 }}
                  transition={loading ? { duration: 1.6, repeat: Infinity } : { duration: 0.3 }}
                >auto_awesome</motion.span>
                <span className="font-headline font-semibold text-on-surface-variant chat-title">YOKAN AI</span>
                <AnimatePresence mode="wait" initial={false}>
                  {badge && (
                    <motion.span
                      key={badge}
                      className="chat-model"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.18 }}
                    >{badge}</motion.span>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex items-center gap-0.5">
                {speech.supported && (
                  <button type="button" className={`chat-icon-btn ${speakOn ? 'is-on' : ''}`} onClick={toggleSpeak}
                    title={speakOn ? 'Gesprochene Fragen werden laut beantwortet' : 'Antworten nicht vorlesen'}>
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span
                        key={speech.speaking ? 'speaking' : speakOn ? 'on' : 'off'}
                        className="material-symbols-outlined"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={speech.speaking ? { scale: [1, 1.15, 1], opacity: 1 } : { scale: 1, opacity: 1 }}
                        exit={{ scale: 0.5, opacity: 0 }}
                        transition={speech.speaking ? { duration: 0.8, repeat: Infinity } : { duration: 0.18 }}
                      >{speech.speaking ? 'graphic_eq' : speakOn ? 'volume_up' : 'volume_off'}</motion.span>
                    </AnimatePresence>
                  </button>
                )}
                <AnimatePresence initial={false}>
                  {messages.length > 0 && !loading && (
                    <motion.button
                      key="clear"
                      type="button"
                      className="chat-icon-btn"
                      onClick={() => { speech.cancel(); setMessages([]) }}
                      title="Neues Gespräch"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.6 }}
                    >
                      <span className="material-symbols-outlined">edit_square</span>
                    </motion.button>
                  )}
                </AnimatePresence>
                {!phone && (
                  <button type="button" className="chat-icon-btn" onClick={() => setMaximized(v => !v)} title={maximized ? 'Verkleinern' : 'Vergrößern'}>
                    <span className="material-symbols-outlined">{maximized ? 'close_fullscreen' : 'open_in_full'}</span>
                  </button>
                )}
                <button type="button" className="chat-icon-btn" onClick={close} title="Schließen">
                  <span className="material-symbols-outlined">{phone ? 'keyboard_arrow_down' : 'close'}</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} onScroll={onScroll} className={`chat-scroll ${phone ? 'chat-scroll-lg' : ''}`}>
              {messages.length === 0
                ? <EmptyState name={firstName} phone={phone} voice={voice.supported} onPick={t => sendMessage(t)} context={context} />
                : (
                  <div className={`flex flex-col ${phone ? 'gap-3' : 'gap-2.5'} ${maximized ? 'chat-thread-wide' : ''}`}>
                    <AnimatePresence initial={false}>
                      {messages.map(m => (
                        <MessageBubble key={m.id} m={m} onConfirm={answerConfirm} busy={loading} voiceHint={voice.supported} />
                      ))}
                    </AnimatePresence>
                  </div>
                )}
            </div>

            <AnimatePresence>
              {!atBottom && messages.length > 0 && (
                <motion.button
                  key="to-bottom"
                  type="button"
                  className="chat-to-bottom"
                  onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })}
                  initial={{ opacity: 0, y: 10, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.8 }}
                  transition={{ duration: 0.2, ease: EASE_OUT }}
                  title="Nach unten"
                >
                  <span className="material-symbols-outlined">arrow_downward</span>
                </motion.button>
              )}
            </AnimatePresence>

            {/* In a conversation: the things to do right now, above the input */}
            <AnimatePresence>
              {messages.length > 0 && !loading && !voice.listening && (context ?? []).some(s => s.tone !== 'info') && (
                <motion.div
                  key="ctx-chips"
                  className="chat-chips-row no-scrollbar"
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8, transition: { duration: 0.12 } }}
                  transition={{ duration: 0.22, ease: EASE_OUT }}
                >
                  {context.filter(s => s.tone !== 'info').slice(0, 3).map((s, i) => (
                    <SuggestionChip key={s.id} s={s} phone={phone} i={i} compact onPick={t => sendMessage(t)} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <div className={maximized ? 'chat-thread-wide w-full' : 'w-full'}>
              <ChatComposer
                phone={phone}
                value={input}
                onChange={setInput}
                onSend={t => sendMessage(t)}
                busy={loading}
                voice={{ ...voice, start: startVoice }}
                inputRef={inputRef}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default memo(ChatPanel)
